import React, { useEffect, useState } from "react";
import api, { fileUrl, uploadUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { User } from "lucide-react";

export default function WelcomePage() {
    const { lang, pick, isRTL } = useI18n();
    const { settings } = useSiteSettings();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/welcome").then((r) => setData(r.data)).catch(() => setData({}));
    }, []);

    if (!data) {
        return <div className="py-40 text-center text-[#6B7280]">…</div>;
    }

    const body = pick(data, "body");
    const paragraphs = String(body || "").split(/\n+/).filter(Boolean);
    const photo = data.photo ? fileUrl(data.photo) : uploadUrl("founder_portrait.png");
    const fallbackTitle = pick(data, "title") || (isRTL ? "كلمة الترحيب" : "Welcome Message");
    const overline = settings[`section_welcome_overline_${lang}`] || fallbackTitle;
    const title = settings[`section_welcome_title_${lang}`] || fallbackTitle;

    return (
        <div data-testid="welcome-page" className="bg-white overflow-hidden">
            <section className="bg-[#EFF0F1] py-10 sm:py-14 md:py-20">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-12">
                    <div className="section-eyebrow mb-5 text-[#0060A7] plus-mark">
                        {overline}
                    </div>
                    <h1 className="heading text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.12] text-[#002951] font-black break-words">
                        {title}
                    </h1>
                </div>
            </section>

            <section className="py-10 sm:py-14 md:py-20">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 lg:gap-16 items-start">
                    {/* Photo column */}
                    <div className="md:col-span-5 lg:col-span-4 w-full max-w-[320px] sm:max-w-[380px] md:max-w-none mx-auto md:mx-0" data-testid="welcome-photo-col">
                        <div className="relative p-2 sm:p-3 bg-[#002951]">
                            <div className="relative aspect-[3/4] bg-[#002951] overflow-hidden">
                                {photo ? (
                                    <img
                                        src={photo}
                                        alt={pick(data, "signature_name")}
                                        loading="eager"
                                        fetchPriority="high"
                                        decoding="async"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-white/40">
                                        <User size={80} strokeWidth={1} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Text column */}
                    <div className="md:col-span-7 lg:col-span-8 min-w-0" dir={isRTL ? "rtl" : "ltr"}>
                        {pick(data, "intro") && (
                            <p className="heading text-lg sm:text-xl md:text-2xl text-[#002951] mb-5 md:mb-6 font-bold leading-snug break-words">
                                {pick(data, "intro")}
                            </p>
                        )}
                        <div className="space-y-4 md:space-y-5 text-base md:text-lg leading-[1.8] md:leading-[1.9] text-[#1f2937] break-words">
                            {paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>

                        {/* Signature */}
                        <div className="mt-9 md:mt-12 pt-6 md:pt-8 border-t border-black/10">
                            <div className="heading text-lg sm:text-xl md:text-2xl text-[#002951] font-extrabold break-words" data-testid="welcome-signature-name">
                                {pick(data, "signature_name")}
                            </div>
                            <div className="text-sm md:text-base text-[#0060A7] mt-1" data-testid="welcome-signature-title">
                                {pick(data, "signature_title")}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
